import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { fetchApplication } from '../../../actions';
import AdminShell from '../layout/AdminShell';
import Card from '../ui/Card';
import Button from '../ui/Button';
import ApplicantListCard from '../application/ApplicantListCard';
import FactFindCard from '../application/FactFindCard';
import VOISection from '../application/VOISection';
import BankStatementsSection from '../application/BankStatementsSection';
import DocumentsSection from '../application/DocumentsSection';
import PrivacyConsentCard from '../application/PrivacyConsentCard';
import ResetDataModal from '../application/ResetDataModal';
import { WarningIcon } from '../ui/icons';

// Redesign of newadmin/ClientPage.js. Data flow is identical:
// fetchApplication(id) once a token exists, and nothing renders until the
// active application in the store matches the route id.
const ApplicationDetailPage = () => {
    const { id } = useParams();
    const application = useSelector((s) => s.admin.active_application.application);
    const user = useSelector((s) => s.user);
    const dispatch = useDispatch();

    const [resetOpen, setResetOpen] = useState(false);

    // Port of componentDidMount/componentDidUpdate: refetch when the token
    // arrives or the route id changes.
    useEffect(() => {
        if (user.token != null) {
            dispatch(fetchApplication(id));
        }
    }, [user.token, id, dispatch]);

    const loaded = application != undefined && application.id == id;

    return (
        <AdminShell title={loaded ? application.name : 'Application'} maxWidth="max-w-7xl">
            {!loaded ? (
                <div className="space-y-4">
                    <div className="rounded-2xl bg-white border border-[#E5E7EB] h-32 animate-pulse" />
                    <div className="rounded-2xl bg-white border border-[#E5E7EB] h-64 animate-pulse" />
                </div>
            ) : (
                <>
                    <ResetDataModal open={resetOpen} onClose={() => setResetOpen(false)} />

                    <div className="mb-5">
                        <h2 className="text-xl sm:text-2xl font-bold text-[#1F2937]">{application.name}</h2>
                        {application.created ? (
                            <p className="text-sm text-[#6B7280] mt-0.5">
                                Created {moment(application.created).format('DD/MM/YYYY')}
                                {application.created_by ? ` by ${application.created_by}` : ''}
                            </p>
                        ) : null}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 items-start">
                        <div className="lg:col-span-4">
                            <FactFindCard />
                        </div>

                        <div className="lg:col-span-8">
                            <ApplicantListCard />
                        </div>

                        <div className="lg:col-span-12">
                            <VOISection />
                        </div>

                        <div className="lg:col-span-12">
                            <BankStatementsSection />
                        </div>

                        <div className="lg:col-span-12">
                            <DocumentsSection />
                        </div>

                        <div className="lg:col-span-12">
                            <PrivacyConsentCard />
                        </div>

                        <div className="lg:col-span-12">
                            <Card
                                title="Danger Zone"
                                subtitle="Reset collected data for this application."
                                icon={<WarningIcon className="h-5 w-5" />}
                            >
                                <Button variant="danger" onClick={() => setResetOpen(true)}>
                                    Reset Data
                                </Button>
                            </Card>
                        </div>
                    </div>
                </>
            )}
        </AdminShell>
    );
};

export default ApplicationDetailPage;
